import type { SurveyPoint } from './typesCore';

/** GEOLOGY_PLUS_CUSTOM is retained only for reading old saved projects. */
export type NaturalFractureSourceMode = 'GEOLOGY' | 'CUSTOM' | 'GEOLOGY_PLUS_CUSTOM';

export type RawGeologyNaturalFracture = Record<string, unknown> & {
  id?: number | string;
  md?: number;
  tvd?: number;
  dip_angle?: number;
  dip_azimuth?: number;
  length_m?: number;
  radius_m?: number;
};

export type NaturalFractureSetDefinition = {
  id: string;
  name: string;
  regenerate: boolean;
  averageLengthM: number;
  stdLengthM: number;
  averageOrientationDeg: number;
  stdOrientationDeg: number;
  averageSpacingM: number;
  stdSpacingM: number;
};

export type NaturalFractureBoundary = {
  centerX: number;
  centerY: number;
  widthM: number;
  heightM: number;
  centerZ: number;
};

export type CustomNaturalFracture = {
  fracture_id: string;
  fracture_set_id: string;
  center_x: number;
  center_y: number;
  center_z: number;
  length: number;
  height: number;
  orientation: number;
  dip: number;
  source: 'CUSTOM';
};

export type NaturalFractureDesignDTO = {
  source_mode: NaturalFractureSourceMode;
  raw_geology_fractures: RawGeologyNaturalFracture[];
  custom_fractures: CustomNaturalFracture[];
  fracture_sets: NaturalFractureSetDefinition[];
  design_metadata: {
    model_version: 'NATURAL_FRACTURE_DESIGN_V1';
    coordinate_system: 'PROJECT_LOCAL_EAST_NORTH_TVD_V1';
    orientation_convention: '0_DEG_NORTH_90_DEG_EAST';
    random_seed: number;
    boundary: NaturalFractureBoundary;
  };
};

export type NaturalFracturePreviewSegment = {
  id: string;
  source: 'GEOLOGY' | 'CUSTOM';
  setId?: string;
  centerX: number;
  centerY: number;
  lengthM: number;
  orientationDeg: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

export type ViewerNaturalFracture = Record<string, unknown> & {
  id: string;
  source: 'GEOLOGY' | 'CUSTOM';
};

export const DEFAULT_NATURAL_FRACTURE_SET: NaturalFractureSetDefinition = {
  id: 'NF-SET-1', name: 'Set1', regenerate: true,
  averageLengthM: 20, stdLengthM: 5,
  averageOrientationDeg: 45, stdOrientationDeg: 10,
  averageSpacingM: 20, stdSpacingM: 4,
};

export const userNaturalFractureSourceMode = (
  mode: NaturalFractureSourceMode | string | undefined,
): 'GEOLOGY' | 'CUSTOM' => mode === 'GEOLOGY' ? 'GEOLOGY' : 'CUSTOM';

export const naturalFractureSourceLabel = (mode: NaturalFractureSourceMode | string): string =>
  userNaturalFractureSourceMode(mode) === 'GEOLOGY' ? '地质条件' : '自定义条件';

export const normalizeNorthAzimuth = (value: number): number => ((value % 360) + 360) % 360;

export const normalizeNaturalFractureSetName = (name: string | undefined, index: number): string => {
  const value = String(name ?? '').trim();
  const legacy = /^(?:自定义裂缝组|Set)\s*(\d+)$/i.exec(value);
  if (legacy) return `Set${Number(legacy[1])}`;
  return value || `Set${index + 1}`;
};

export const finiteSegmentFromCenter = (
  centerX: number, centerY: number, lengthM: number, orientationDeg: number,
): Pick<NaturalFracturePreviewSegment, 'x1' | 'y1' | 'x2' | 'y2'> => {
  // Project coordinates are X=East and Y=North. This keeps the visible and
  // simulated convention identical: 0°=North, 90°=East.
  const radians = normalizeNorthAzimuth(orientationDeg) * Math.PI / 180;
  const dx = Math.sin(radians) * Math.max(0, lengthM) / 2;
  const dy = Math.cos(radians) * Math.max(0, lengthM) / 2;
  return { x1: centerX - dx, y1: centerY - dy, x2: centerX + dx, y2: centerY + dy };
};

const hashSeed = (seed: number, text: string): number => {
  let value = (seed | 0) ^ 0x9e3779b9;
  for (let index = 0; index < text.length; index += 1) {
    value = Math.imul(value ^ text.charCodeAt(index), 0x85ebca6b);
  }
  return value >>> 0;
};

const createRandom = (seed: number): (() => number) => {
  let state = seed >>> 0 || 1;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
};

const gaussian = (random: () => number): number => {
  const first = Math.max(1e-12, random());
  return Math.sqrt(-2 * Math.log(first)) * Math.cos(2 * Math.PI * random());
};

const positiveSample = (average: number, deviation: number, random: () => number, minimum: number): number => {
  const safeAverage = Math.max(minimum, Number.isFinite(average) ? average : minimum);
  const safeDeviation = Math.max(0, Number.isFinite(deviation) ? deviation : 0);
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const sampled = safeAverage + gaussian(random) * safeDeviation;
    if (sampled >= minimum && sampled <= Math.max(minimum, safeAverage + 4 * safeDeviation)) return sampled;
  }
  return safeAverage;
};

export const generateCustomNaturalFractures = (
  sets: NaturalFractureSetDefinition[],
  boundary: NaturalFractureBoundary,
  seed: number,
  previous: CustomNaturalFracture[] = [],
): CustomNaturalFracture[] => {
  const retained = previous.filter((fracture) => {
    const set = sets.find((item) => item.id === fracture.fracture_set_id);
    return set && !set.regenerate;
  });
  const generated = sets.flatMap((set) => {
    if (!set.regenerate) return [];
    const random = createRandom(hashSeed(seed, set.id));
    const meanSpacing = Math.max(0.5, set.averageSpacingM);
    const area = Math.max(1, boundary.widthM) * Math.max(1, boundary.heightM);
    const count = Math.max(1, Math.min(5000, Math.round(area / (meanSpacing * meanSpacing))));
    const columns = Math.max(1, Math.ceil(Math.sqrt(count * boundary.widthM / Math.max(1, boundary.heightM))));
    const rows = Math.max(1, Math.ceil(count / columns));
    const cellWidth = boundary.widthM / columns;
    const cellHeight = boundary.heightM / rows;
    return Array.from({ length: count }, (_, index): CustomNaturalFracture => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const sampledSpacing = positiveSample(set.averageSpacingM, set.stdSpacingM, random, 0.5);
      const spacingScale = Math.min(1.5, Math.max(0.35, sampledSpacing / meanSpacing));
      const length = positiveSample(set.averageLengthM, set.stdLengthM, random, 0.1);
      return {
        fracture_id: `CUSTOM-${set.id}-${seed}-${index + 1}`,
        fracture_set_id: set.id,
        center_x: boundary.centerX - boundary.widthM / 2 + (column + 0.5) * cellWidth
          + (random() - 0.5) * cellWidth * 0.75 * spacingScale,
        center_y: boundary.centerY - boundary.heightM / 2 + (row + 0.5) * cellHeight
          + (random() - 0.5) * cellHeight * 0.75 * spacingScale,
        center_z: boundary.centerZ,
        length,
        height: Math.max(1, length / 2),
        orientation: normalizeNorthAzimuth(set.averageOrientationDeg + gaussian(random) * Math.max(0, set.stdOrientationDeg)),
        dip: 90,
        source: 'CUSTOM',
      };
    });
  });
  return [...retained, ...generated];
};

export const interpolateSurveyXY = (
  survey: SurveyPoint[], md: number,
): { x: number; y: number; z: number } | null => {
  const sorted = [...survey].filter((point) => Number.isFinite(point.md)).sort((a, b) => a.md - b.md);
  if (sorted.length < 2 || md < sorted[0].md || md > sorted[sorted.length - 1].md) return null;
  const found = sorted.findIndex((point) => point.md >= md);
  const rightIndex = Math.max(1, found < 0 ? sorted.length - 1 : found);
  const right = sorted[rightIndex];
  const left = sorted[rightIndex - 1];
  const fraction = right.md === left.md ? 0 : (md - left.md) / (right.md - left.md);
  const lerp = (first: number, second: number) => first + fraction * (second - first);
  return { x: lerp(left.ew, right.ew), y: lerp(left.ns, right.ns), z: lerp(left.tvd, right.tvd) };
};

export const geologyPreviewSegments = (
  rawFractures: RawGeologyNaturalFracture[], survey: SurveyPoint[],
): NaturalFracturePreviewSegment[] => rawFractures.flatMap((raw, index) => {
  const explicitX = Number(raw.center_x ?? raw.centerX ?? raw.x);
  const explicitY = Number(raw.center_y ?? raw.centerY ?? raw.y);
  const md = Number(raw.md ?? raw.MD);
  const interpolated = Number.isFinite(md) ? interpolateSurveyXY(survey, md) : null;
  const centerX = Number.isFinite(explicitX) ? explicitX : interpolated?.x;
  const centerY = Number.isFinite(explicitY) ? explicitY : interpolated?.y;
  const radius = Number(raw.radius_m ?? raw.radiusM);
  const rawLength = Number(raw.length_m ?? raw.lengthM ?? raw.majorLengthM);
  const lengthM = rawLength > 0 ? rawLength : radius > 0 ? 2 * radius : 0;
  const dipDirection = Number(raw.dip_azimuth ?? raw.dipAzimuth ?? raw.dipDirectionDeg);
  const orientationDeg = Number(raw.orientation ?? raw.strike_deg ?? raw.strikeDeg
    ?? (Number.isFinite(dipDirection) ? dipDirection - 90 : Number.NaN));
  if (!Number.isFinite(centerX) || !Number.isFinite(centerY) || !Number.isFinite(orientationDeg) || lengthM <= 0) return [];
  const resolvedX = Number(centerX);
  const resolvedY = Number(centerY);
  return [{
    id: `GEOLOGY-${String(raw.id ?? index + 1)}`, source: 'GEOLOGY' as const,
    centerX: resolvedX, centerY: resolvedY, lengthM, orientationDeg: normalizeNorthAzimuth(orientationDeg),
    ...finiteSegmentFromCenter(resolvedX, resolvedY, lengthM, orientationDeg),
  }];
});

export const customPreviewSegments = (fractures: CustomNaturalFracture[]): NaturalFracturePreviewSegment[] => fractures.map((fracture) => ({
  id: fracture.fracture_id, source: 'CUSTOM', setId: fracture.fracture_set_id,
  centerX: fracture.center_x, centerY: fracture.center_y,
  lengthM: fracture.length, orientationDeg: fracture.orientation,
  ...finiteSegmentFromCenter(fracture.center_x, fracture.center_y, fracture.length, fracture.orientation),
}));

export const rawInputFracturesForMode = (design: NaturalFractureDesignDTO): Array<Record<string, unknown>> => {
  if (design.source_mode === 'GEOLOGY') return design.raw_geology_fractures.map((item) => ({ ...item }));
  return design.custom_fractures.map((item) => ({ ...item }));
};

/** Convert the applied design into the same finite-geometry DTO shape used by the Viewer. */
export const naturalFractureViewerRecords = (
  design: NaturalFractureDesignDTO,
): ViewerNaturalFracture[] => {
  if (design.source_mode === 'GEOLOGY') {
    return design.raw_geology_fractures.map((fracture, index) => ({
      ...fracture,
      id: String(fracture.id ?? `GEOLOGY-DESIGN-${index + 1}`),
      source: 'GEOLOGY' as const,
    }));
  }
  return design.custom_fractures.map((fracture) => ({
    id: fracture.fracture_id,
    source: 'CUSTOM' as const,
    centerXYZ: [fracture.center_x, fracture.center_y, fracture.center_z],
    // Custom orientation is strike azimuth (0° North, 90° East), while the
    // canonical Viewer DTO stores dip direction.
    dipDirectionDeg: normalizeNorthAzimuth(fracture.orientation + 90),
    dipDeg: fracture.dip,
    majorLengthM: fracture.length,
    minorLengthM: fracture.height,
  }));
};

export const normalizeNaturalFractureDesign = (
  design: NaturalFractureDesignDTO,
): NaturalFractureDesignDTO => ({
  ...design,
  source_mode: userNaturalFractureSourceMode(design.source_mode),
  fracture_sets: design.fracture_sets.length
    ? design.fracture_sets.map((set, index) => ({
      ...set,
      name: normalizeNaturalFractureSetName(set.name, index),
    }))
    : [{ ...DEFAULT_NATURAL_FRACTURE_SET }],
});

export const defaultNaturalFractureDesign = (
  rawGeologyFractures: RawGeologyNaturalFracture[], boundary: NaturalFractureBoundary,
): NaturalFractureDesignDTO => ({
  source_mode: 'GEOLOGY',
  raw_geology_fractures: rawGeologyFractures.map((fracture) => ({ ...fracture })),
  custom_fractures: [], fracture_sets: [{ ...DEFAULT_NATURAL_FRACTURE_SET }],
  design_metadata: {
    model_version: 'NATURAL_FRACTURE_DESIGN_V1',
    coordinate_system: 'PROJECT_LOCAL_EAST_NORTH_TVD_V1',
    orientation_convention: '0_DEG_NORTH_90_DEG_EAST',
    random_seed: 20260908, boundary: { ...boundary },
  },
});
